import { SessionSummary } from '@bioliberty/lifemind';
import { useMemo } from 'react';
import { useAuthentication } from '../../auth';
import { useTenantIds } from '../../hooks/useTenantIds';

export interface MonthlySessionsSummary {
  clinic: string;
  year: number;
  month: number;
  patients: number;
  sessions: number;
  totalReps: number;
  totalTimeTaken: number;
  repsPerPatient: number;
  repsPerSession: number;
  timeTakenPerPatient: number;
  timeTakenPerSession: number;
  sessionsPerPatient: number;
}

export function useMonthlySessions(sessions: SessionSummary[]): MonthlySessionsSummary[] {
  const currentUser = useAuthentication();
  const tenantIds = useTenantIds();

  const rows = useMemo(() => {
    const sessionsGroupedByYearMonthSubtenant = sessions.reduce((acc: Record<string, SessionSummary[]>, s) => {
      const date = new Date(s.startedOn);
      const key = `${date.getFullYear()}_${date.getMonth()}_${s.tenantId}`;
      (acc[key] ||= []).push(s);
      return acc;
    }, {});

    return Object.entries(sessionsGroupedByYearMonthSubtenant)
      .map(([key, sessions]) => {
        const [year, month, tenantId] = key.split('_');
        const clinic = currentUser.subtenants?.find(({ name }) => name === tenantId)?.label || tenantId;

        const patients = new Set(sessions.map((s) => s.patientId));
        const totalReps = sessions.reduce((sum, s) => sum + s.totalReps, 0);
        const totalTimeTaken = sessions.reduce((sum, s) => sum + s.totalTimeTaken, 0);

        return {
          year: parseInt(year, 10),
          month: parseInt(month, 10),
          tenantId,
          clinic,
          patients: patients.size,
          sessions: sessions.length,
          totalReps,
          totalTimeTaken,
          repsPerPatient: patients.size > 0 ? totalReps / patients.size : 0,
          repsPerSession: sessions.length > 0 ? totalReps / sessions.length : 0,
          timeTakenPerPatient: patients.size > 0 ? totalTimeTaken / patients.size : 0,
          timeTakenPerSession: sessions.length > 0 ? totalTimeTaken / sessions.length : 0,
          sessionsPerPatient: patients.size > 0 ? sessions.length / patients.size : 0
        };
      })
      .filter((row) => tenantIds?.includes(row.tenantId));
  }, [sessions, currentUser, tenantIds]);

  return rows;
}
